import { TellescopeModelFromName, usePermission, useTellescopeMutation } from "@tellescope/tanstack-query";
import { Badge, cn } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { BadgeDropdownCell } from "../../tables";
import { REVERSE_STATUS_TRANSLATIONS } from "../constants";
import { getMedStatusTranslations } from "@tellescope/utils";

export interface FlagsCellProps
  extends Pick<TellescopeModelFromName<"enduser_observations">, "medStatus" | "beforeMeal" | "irregularHeartbeat"> {
  className?: string;
  observationIds: string[];
  showMealFlag?: boolean;
  showMedStatusFlag?: boolean;
  showHeartbeatFlag?: boolean;
}

export const FlagsCell = ({
  medStatus,
  beforeMeal,
  irregularHeartbeat,
  observationIds,
  className,
  showMealFlag,
  showMedStatusFlag,
  showHeartbeatFlag,
}: FlagsCellProps) => {
  const { t } = useTranslation("vitals");

  const updateObservationMutation = useTellescopeMutation({
    model: "enduser_observations",
    method: "updateOne",
  });

  const updateObservationFlag = <
    T extends Extract<keyof FlagsCellProps, "beforeMeal" | "irregularHeartbeat" | "medStatus">,
  >(
    flag: T,
    value: NonNullable<FlagsCellProps[T]>
  ) => {
    observationIds.forEach((id) =>
      updateObservationMutation.mutate({
        id,
        [flag]: value,
      })
    );
  };

  const { access: canEdit } = usePermission({
    model: "enduser_observations",
    action: "update",
  });

  const resolvedShowMealFlag = showMealFlag || beforeMeal !== undefined;
  const resolvedShowMedStatusFlag = showMedStatusFlag || medStatus !== undefined;
  const resolvedShowHeartbeatFlag = showHeartbeatFlag || irregularHeartbeat !== undefined;

  return (
    <div className={cn("flex gap-1 flex-wrap text-muted-foreground text-sm", className)}>
      {resolvedShowMealFlag &&
        (canEdit ? (
          <BadgeDropdownCell
            value={beforeMeal === undefined ? t("Unknown") : beforeMeal ? t("Before Meal") : t("After Meal")}
            options={[t("Before Meal"), t("After Meal")]}
            onChange={(val) => {
              const boolVal = val === t("Before Meal");
              updateObservationFlag("beforeMeal", boolVal);
            }}
            unknownLabel=""
            unselectedBadgeVariant={beforeMeal === undefined ? "placeholder" : "outline"}
            title={t("Meal Timing")}
            size="large"
            selectedBadgeVariant="default"
          />
        ) : (
          <Badge variant="outline">
            {beforeMeal === undefined ? t("Unknown") : beforeMeal ? t("Before Meal") : t("After Meal")}
          </Badge>
        ))}
      {resolvedShowMedStatusFlag &&
        (canEdit ? (
          <BadgeDropdownCell
            value={
              medStatus ? getMedStatusTranslations()[medStatus] || medStatus.replace("Medication: ", "") : t("Unknown")
            }
            options={Object.values(getMedStatusTranslations())}
            onChange={(val) => {
              const newVal = val ? REVERSE_STATUS_TRANSLATIONS[val] : undefined;
              if (newVal) {
                updateObservationFlag("medStatus", newVal);
              }
            }}
            unknownLabel=""
            unselectedBadgeVariant="outline"
            title={t("Medification Status")}
            size="large"
            selectedBadgeVariant="default"
          />
        ) : (
          medStatus !== undefined && (
            <Badge variant="outline">
              {getMedStatusTranslations()[medStatus] || medStatus.replace("Medication: ", "")}
            </Badge>
          )
        ))}
      {resolvedShowHeartbeatFlag &&
        (canEdit ? (
          <BadgeDropdownCell
            value={
              irregularHeartbeat === undefined ? t("Heart Rhythm") : irregularHeartbeat ? t("Irregular") : t("Regular")
            }
            options={[t("Irregular"), t("Regular")]}
            onChange={(val) => {
              const boolVal = val === t("Irregular");
              updateObservationFlag("irregularHeartbeat", boolVal);
            }}
            unknownLabel=""
            valueBadgeVariant={irregularHeartbeat === undefined ? "placeholder" : "outline"}
            title={t("Heart Rhythm")}
            size="large"
            selectedBadgeVariant="default"
          />
        ) : (
          <Badge variant="outline">{irregularHeartbeat ? t("Irregular") : t("Regular")}</Badge>
        ))}
    </div>
  );
};
